import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import CSRLandscapePage from "./pages/CSRLandscapePage";
import StartFundingPage from "./pages/StartFundingPage";
import ExploreProjectsPage from "./pages/ExploreProjectsPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import XchangeLanding from "./pages/XchangeLanding";
import XchangeRegister from "./pages/XchangeRegister";
import XchangeLogin from "./pages/XchangeLogin";
import CorporateDashboard from "./pages/CorporateDashboard";
import NGODashboard from "./pages/NGODashboard";
import AdminPanel from "./pages/AdminPanel";
import AdminSubmissionsPage from "./pages/AdminSubmissionsPage";
import AdminInterestExpressionsPage from "./pages/AdminInterestExpressionsPage";
import PublicListingsPage from "./pages/PublicListingsPage";
import OngoingProjectsPage from "./pages/OngoingProjectsPage";
import ImpactDashboard from "./pages/ImpactDashboard";
import GovernanceDashboard from "./pages/GovernanceDashboard";
import LoginPage from "./pages/LoginPage";
import SaaSSponsorDashboard from "./pages/SaaSSponsorDashboard";
import SaaSAgencyDashboard from "./pages/SaaSAgencyDashboard";
import SaaSAdminDashboard from "./pages/SaaSAdminDashboard";
import EngagementDetailPage from "./pages/EngagementDetailPage";
import AdminSaaSManagement from "./pages/AdminSaaSManagement";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import ReportGeneration from "./pages/ReportGeneration";
import ProjectManagementHub from "./pages/ProjectManagementHub";
import AdminResourceLibrary from "./pages/AdminResourceLibrary";
import EnhancedSponsorDashboard from "./pages/EnhancedSponsorDashboard";
import EnhancedNGODashboard from "./pages/EnhancedNGODashboard";
import ProjectCreationWizard from "./pages/ProjectCreationWizard";
import MilestoneTrackerPage from "./pages/MilestoneTrackerPage";
import NGOMatchingPage from "./pages/NGOMatchingPage";
import ComplianceVaultPage from "./pages/ComplianceVaultPage";
import ComplianceCalendarPage from "./pages/ComplianceCalendarPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import SROICalculatorPage from "./pages/SROICalculatorPage";
import ImpactStoryGeneratorPage from "./pages/ImpactStoryGeneratorPage";
import CSRPolicyBuilderPage from "./pages/CSRPolicyBuilderPage";
import KnowledgeHubPage from "./pages/KnowledgeHubPage";
import KnowledgeArticleDetailPage from "./pages/KnowledgeArticleDetailPage";
import CACSPartnerDashboard from "./pages/CACSPartnerDashboard";
import ConsultantDashboard from "./pages/ConsultantDashboard";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";
import KnowledgeHubAdminPanel from "./pages/KnowledgeHubAdminPanel";
import HelpSupportPage from "./pages/HelpSupportPage";
import VerifiedPartnersPage from "./pages/VerifiedPartnersPage";
import AboutUsPage from "./pages/AboutUsPage";
import MainAdminDashboard from "./pages/MainAdminDashboard";
import HiddenAdminLogin from "./pages/HiddenAdminLogin";
import GovernanceSuitePage from "./pages/GovernanceSuitePage";
import { Toaster } from "./components/ui/sonner";

// Simple page transition wrapper using CSS animations
const PageWrapper = ({ children }) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage("fadeOut");
    }
  }, [location, displayLocation]);

  const handleAnimationEnd = () => {
    if (transitionStage === "fadeOut") {
      setTransitionStage("fadeIn");
      setDisplayLocation(location);
    }
  };

  return (
    <div
      className={`page-transition ${transitionStage}`}
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const xchangeToken = localStorage.getItem('xchange_token');
    setIsAuthenticated(!!(token || xchangeToken));
  }, []);

  const ProtectedRoute = ({ children }) => {
    const token = localStorage.getItem('token');
    const xchangeToken = localStorage.getItem('xchange_token');
    return (token || xchangeToken) ? children : <Navigate to="/login" />;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <PageWrapper>
          <Routes>
            {/* Main Pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/start-funding" element={<StartFundingPage />} />
            <Route path="/explore-projects" element={<ExploreProjectsPage />} />
            <Route path="/csr-landscape" element={<CSRLandscapePage />} />
            <Route path="/governance" element={<GovernanceSuitePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            
            {/* Xchange - Now merged with Homepage, redirect to root */}
            <Route path="/xchange" element={<Navigate to="/" replace />} />
            <Route path="/xchange/:role/register" element={<XchangeRegister />} />
            <Route path="/xchange/login" element={<XchangeLogin />} />
            <Route path="/xchange/corporate/dashboard" element={<CorporateDashboard />} />
            <Route path="/xchange/ngo/dashboard" element={<NGODashboard />} />
            <Route path="/xchange/admin/panel" element={<Navigate to="/tmg-admin-console-x7k9" replace />} />
            <Route path="/xchange/admin/submissions" element={<Navigate to="/tmg-admin-console-x7k9" replace />} />
            <Route path="/xchange/admin/interests" element={<Navigate to="/tmg-admin-console-x7k9" replace />} />
            <Route path="/xchange/listings" element={<Navigate to="/" replace />} />
            <Route path="/xchange/ongoing-projects" element={<OngoingProjectsPage />} />
            
            {/* Verified Partners Pages */}
            <Route path="/partners/sponsors" element={<VerifiedPartnersPage />} />
            <Route path="/partners/ngos" element={<VerifiedPartnersPage />} />
            
            {/* HIDDEN ADMIN PORTAL - SECRET URLs (not linked from public site) */}
            <Route path="/tmg-secure-portal-2025" element={<HiddenAdminLogin />} />
            <Route path="/tmg-admin-console-x7k9" element={<MainAdminDashboard />} />
            
            {/* Redirect old admin routes to hidden portal */}
            <Route path="/admin/dashboard" element={<Navigate to="/tmg-admin-console-x7k9" replace />} />
            
            {/* Project Monitoring & Reporting Layer */}
            <Route path="/project-management" element={<ProjectManagementHub />} />
            <Route path="/saas/sponsor/dashboard" element={<SaaSSponsorDashboard />} />
            <Route path="/saas/agency/dashboard" element={<SaaSAgencyDashboard />} />
            <Route path="/saas/admin/dashboard" element={<SaaSAdminDashboard />} />
            <Route path="/saas/admin/manage" element={<AdminSaaSManagement />} />
            <Route path="/saas/admin/analytics" element={<AnalyticsDashboard />} />
            <Route path="/saas/admin/reports" element={<ReportGeneration />} />
            <Route path="/saas/engagement/:id" element={<EngagementDetailPage />} />
            
            {/* Admin Resource Library */}
            <Route path="/admin/resources" element={<AdminResourceLibrary />} />
            
            {/* Enhanced Dashboards */}
            <Route path="/enhanced/sponsor/dashboard" element={<EnhancedSponsorDashboard />} />
            <Route path="/enhanced/ngo/dashboard" element={<EnhancedNGODashboard />} />
            
            {/* Project Management */}
            <Route path="/projects/create" element={<ProjectCreationWizard />} />
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            
            {/* Milestone Tracker */}
            <Route path="/milestones/:projectId" element={<MilestoneTrackerPage />} />
            
            {/* Matching Interface */}
            <Route path="/matching/browse" element={<NGOMatchingPage />} />
            
            {/* Compliance */}
            <Route path="/compliance/vault" element={<ComplianceVaultPage />} />
            <Route path="/compliance/calendar" element={<ComplianceCalendarPage />} />
            
            {/* Impact Intelligence Layer */}
            <Route path="/impact/sroi-calculator" element={<SROICalculatorPage />} />
            <Route path="/impact/story-generator" element={<ImpactStoryGeneratorPage />} />
            
            {/* Consultancy Layer */}
            <Route path="/consultancy/policy-builder" element={<CSRPolicyBuilderPage />} />
            
            {/* Knowledge Hub */}
            <Route path="/knowledge" element={<KnowledgeHubPage />} />
            <Route path="/knowledge/article/:articleId" element={<KnowledgeArticleDetailPage />} />
            <Route path="/knowledge/admin" element={<KnowledgeHubAdminPanel />} />
            
            {/* Advanced User Roles */}
            <Route path="/cacs-partner/dashboard" element={<CACSPartnerDashboard />} />
            <Route path="/consultant/dashboard" element={<ConsultantDashboard />} />
            
            {/* Notifications */}
            <Route path="/notifications/settings" element={<NotificationSettingsPage />} />
            
            {/* Help & Support */}
            <Route path="/help" element={<HelpSupportPage />} />
            
            {/* Redirects for old paths */}
            <Route path="/about" element={<Navigate to="/" replace />} />
            <Route path="/for-corporates" element={<Navigate to="/xchange" replace />} />
            <Route path="/for-ngos" element={<Navigate to="/xchange" replace />} />
            <Route path="/platform" element={<Navigate to="/xchange" replace />} />
            <Route path="/product" element={<Navigate to="/xchange" replace />} />
            <Route path="/products" element={<Navigate to="/xchange" replace />} />
            <Route path="/company" element={<Navigate to="/" replace />} />
            <Route path="/solutions" element={<Navigate to="/xchange" replace />} />
            <Route path="/impact-pool" element={<Navigate to="/xchange" replace />} />
            <Route path="/intelligence-suite" element={<Navigate to="/xchange" replace />} />
            <Route path="/advisory" element={<Navigate to="/xchange" replace />} />
            
            {/* Auth & Dashboard - Redirect old login to new unified login */}
            <Route path="/login" element={<Navigate to="/xchange/login" replace />} />
            <Route
              path="/dashboard/impact"
              element={
                <ProtectedRoute>
                  <ImpactDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/governance"
              element={
                <ProtectedRoute>
                  <GovernanceDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </PageWrapper>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;